import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js';

export type MeshOptions = {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: [number, number, number] | number;
  cast?: boolean;
  receive?: boolean;
};

export function mesh(geometry: THREE.BufferGeometry, material: THREE.Material, options: MeshOptions = {}): THREE.Mesh {
  const item = new THREE.Mesh(geometry, material);
  if (options.position) item.position.set(...options.position);
  if (options.rotation) item.rotation.set(...options.rotation);
  if (options.scale != null) {
    if (typeof options.scale === 'number') item.scale.setScalar(options.scale);
    else item.scale.set(...options.scale);
  }
  item.castShadow = options.cast ?? true;
  item.receiveShadow = options.receive ?? true;
  return item;
}

/** Chunky toy box: RoundedBoxGeometry with a radius clamped to the smallest side. */
export function roundedBox(width: number, height: number, depth: number, radius: number, segments = 1): THREE.BufferGeometry {
  const r = Math.min(radius, width / 2, height / 2, depth / 2);
  return new RoundedBoxGeometry(width, height, depth, segments, r);
}

export function roundedRectShape(width: number, height: number, radius: number): THREE.Shape {
  const x = -width / 2;
  const y = -height / 2;
  const r = Math.min(radius, width / 2, height / 2);
  const shape = new THREE.Shape();
  shape.moveTo(x + r, y);
  shape.lineTo(x + width - r, y);
  shape.quadraticCurveTo(x + width, y, x + width, y + r);
  shape.lineTo(x + width, y + height - r);
  shape.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  shape.lineTo(x + r, y + height);
  shape.quadraticCurveTo(x, y + height, x, y + height - r);
  shape.lineTo(x, y + r);
  shape.quadraticCurveTo(x, y, x + r, y);
  return shape;
}

/** Rounded slab lying on the XZ plane with its base at y = 0. */
export function padGeometry(width: number, depth: number, height: number, radius: number, bevel = 0.06): THREE.ExtrudeGeometry {
  const geometry = new THREE.ExtrudeGeometry(roundedRectShape(width, depth, radius), {
    depth: height,
    bevelEnabled: bevel > 0,
    bevelThickness: bevel,
    bevelSize: bevel,
    bevelSegments: 2,
    curveSegments: 8,
  });
  geometry.rotateX(-Math.PI / 2);
  geometry.computeBoundingBox();
  const minY = geometry.boundingBox?.min.y ?? 0;
  geometry.translate(0, -minY, 0);
  return geometry;
}

export function extrudedPad(
  width: number,
  depth: number,
  height: number,
  radius: number,
  material: THREE.Material | THREE.Material[],
  bevel = 0.06,
): THREE.Mesh {
  const pad = new THREE.Mesh(padGeometry(width, depth, height, radius, bevel), material);
  pad.castShadow = true;
  pad.receiveShadow = true;
  return pad;
}

/** Thin cylinder connecting two points (ropes, cables, thin rails). */
export function rod(from: THREE.Vector3, to: THREE.Vector3, radius: number, material: THREE.Material, segments = 5): THREE.Mesh {
  const length = from.distanceTo(to);
  const item = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, length, segments), material);
  item.position.copy(from).add(to).multiplyScalar(0.5);
  item.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3().subVectors(to, from).normalize());
  item.castShadow = false;
  item.receiveShadow = false;
  return item;
}

/** Flat disc on the XZ plane with its base at y = 0 (used for plazas and aprons). */
export function discGeometry(radius: number, height: number, segments = 40): THREE.CylinderGeometry {
  const geometry = new THREE.CylinderGeometry(radius, radius, height, segments);
  geometry.translate(0, height / 2, 0);
  return geometry;
}

export function canvas2d(width: number, height: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2D context unavailable');
  }
  return [canvas, ctx];
}

export function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 4294967296;
  };
}

export function makeSignTexture(text: string, background: string, foreground: string, trim = foreground): THREE.CanvasTexture {
  const [canvas, ctx] = canvas2d(512, 160);
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, 512, 160);
  ctx.strokeStyle = trim;
  ctx.lineWidth = 10;
  ctx.strokeRect(12, 12, 488, 136);
  ctx.fillStyle = trim;
  for (let i = 0; i < 12; i += 1) {
    ctx.beginPath();
    ctx.arc(30 + i * 41, 30, 6, 0, Math.PI * 2);
    ctx.arc(30 + i * 41, 130, 6, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = 'rgba(0,0,0,0.18)';
  ctx.font = 'bold 84px "Trebuchet MS", "Segoe UI", Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 260, 86);
  ctx.fillStyle = foreground;
  ctx.fillText(text, 256, 80);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  return texture;
}

/** Soft mottled lawn: two close greens with low-contrast blotches so big lawns are not flat. */
export function makeGrassTexture(): THREE.CanvasTexture {
  const [canvas, ctx] = canvas2d(256, 256);
  ctx.fillStyle = '#9bd35c';
  ctx.fillRect(0, 0, 256, 256);
  const random = seededRandom(7);
  const tones = ['#93cc55', '#a4db64', '#8fc853', '#a9de6c'];
  for (let i = 0; i < 260; i += 1) {
    ctx.fillStyle = tones[i % tones.length]!;
    ctx.globalAlpha = 0.3 + random() * 0.25;
    const x = random() * 256;
    const y = random() * 256;
    const r = 8 + random() * 22;
    ctx.beginPath();
    ctx.ellipse(x, y, r, r * (0.55 + random() * 0.5), random() * Math.PI, 0, Math.PI * 2);
    ctx.fill();
    if (x < 40 || y < 40) {
      ctx.beginPath();
      ctx.ellipse(x + 256, y + 256, r, r * 0.7, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.globalAlpha = 1;
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.anisotropy = 4;
  return texture;
}

/** Quiet paving: warm sand with faint grout lines and a few lighter flagstones. */
export function makePavingTexture(): THREE.CanvasTexture {
  const [canvas, ctx] = canvas2d(256, 256);
  ctx.fillStyle = '#f0d8a8';
  ctx.fillRect(0, 0, 256, 256);
  const random = seededRandom(31);
  const cell = 64;
  for (let row = 0; row < 4; row += 1) {
    for (let col = 0; col < 4; col += 1) {
      const shade = 0.6 + random() * 0.4;
      ctx.fillStyle = `rgba(255, 244, 214, ${(shade * 0.5).toFixed(2)})`;
      ctx.fillRect(col * cell + 3, row * cell + 3, cell - 6, cell - 6);
    }
  }
  ctx.strokeStyle = 'rgba(170, 128, 78, 0.28)';
  ctx.lineWidth = 3;
  for (let i = 0; i <= 4; i += 1) {
    ctx.beginPath();
    ctx.moveTo(i * cell, 0);
    ctx.lineTo(i * cell, 256);
    ctx.moveTo(0, i * cell);
    ctx.lineTo(256, i * cell);
    ctx.stroke();
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.anisotropy = 4;
  return texture;
}

export function tileTexture(canvas: HTMLCanvasElement): THREE.CanvasTexture {
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.anisotropy = 4;
  return texture;
}

export function roundedRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

/**
 * Floor-tile textures (Fable floor family). Each canvas is one 0.48 paving subcell, so the
 * pattern lines up with the tile seams: joints in the texture continue into the grout band.
 */

/**
 * Classic (COL-011B redesign): four soft cream pavers per tile with a warm sand joint and a
 * gentle top-left highlight, so the default path reads as friendly pillow-stones instead of
 * one flat slab.
 */
export function makeFlagstoneTileTexture(): THREE.CanvasTexture {
  const [canvas, ctx] = canvas2d(128, 128);
  ctx.fillStyle = '#dcbf8a';
  ctx.fillRect(0, 0, 128, 128);
  const tones = ['#f6e3b8', '#f2dcae', '#f8e7c1', '#efd7a6'];
  const half = 64;
  const joint = 6;
  for (let row = 0; row < 2; row += 1) {
    for (let col = 0; col < 2; col += 1) {
      const x = col * half + joint / 2;
      const y = row * half + joint / 2;
      ctx.fillStyle = tones[(row * 2 + col) % tones.length]!;
      roundedRectPath(ctx, x, y, half - joint, half - joint, 12);
      ctx.fill();
      ctx.fillStyle = 'rgba(255, 250, 236, 0.55)';
      roundedRectPath(ctx, x + 6, y + 5, half - joint - 12, (half - joint) * 0.38, 10);
      ctx.fill();
      ctx.fillStyle = 'rgba(170, 128, 78, 0.16)';
      roundedRectPath(ctx, x + 6, y + half - joint - 12, half - joint - 12, 7, 4);
      ctx.fill();
    }
  }
  return tileTexture(canvas);
}

/**
 * Brick (COL-011B redesign): rosy terracotta basket-weave — each quadrant holds a pair of
 * bricks, alternating horizontal/vertical, on a pale sand mortar. Wraps seamlessly per tile.
 */
export function makeBrickTileTexture(): THREE.CanvasTexture {
  const [canvas, ctx] = canvas2d(128, 128);
  ctx.fillStyle = '#ead6b0';
  ctx.fillRect(0, 0, 128, 128);
  const random = seededRandom(11);
  const tones = ['#d3714a', '#dc7b52', '#c8653f', '#e08459', '#cf6c46'];
  const quad = 64;
  const mortar = 6;
  for (let row = 0; row < 2; row += 1) {
    for (let col = 0; col < 2; col += 1) {
      const horizontal = (row + col) % 2 === 0;
      const ox = col * quad;
      const oy = row * quad;
      for (let i = 0; i < 2; i += 1) {
        const tone = tones[Math.floor(random() * tones.length)]!;
        ctx.fillStyle = tone;
        if (horizontal) {
          const y = oy + i * (quad / 2) + mortar / 2;
          roundedRectPath(ctx, ox + mortar / 2, y, quad - mortar, quad / 2 - mortar, 5);
          ctx.fill();
          ctx.fillStyle = 'rgba(255, 220, 190, 0.3)';
          roundedRectPath(ctx, ox + mortar / 2 + 4, y + 3, quad - mortar - 8, 8, 4);
          ctx.fill();
        } else {
          const x = ox + i * (quad / 2) + mortar / 2;
          roundedRectPath(ctx, x, oy + mortar / 2, quad / 2 - mortar, quad - mortar, 5);
          ctx.fill();
          ctx.fillStyle = 'rgba(255, 220, 190, 0.3)';
          roundedRectPath(ctx, x + 3, oy + mortar / 2 + 4, 8, quad - mortar - 8, 4);
          ctx.fill();
        }
      }
    }
  }
  return tileTexture(canvas);
}

/**
 * Stone (COL-011B redesign — "elegant plaza"): one large pale blue-grey slab with a soft bevel,
 * a slate diamond inlay in the centre and four corner dots, so plazas look designed, not rubble.
 */
export function makeCobbleTileTexture(): THREE.CanvasTexture {
  const [canvas, ctx] = canvas2d(128, 128);
  ctx.fillStyle = '#8b97a6';
  ctx.fillRect(0, 0, 128, 128);
  ctx.fillStyle = '#c9d3dd';
  roundedRectPath(ctx, 4, 4, 120, 120, 10);
  ctx.fill();
  ctx.fillStyle = 'rgba(255, 255, 255, 0.35)';
  roundedRectPath(ctx, 10, 8, 108, 44, 10);
  ctx.fill();
  ctx.fillStyle = 'rgba(80, 96, 116, 0.18)';
  roundedRectPath(ctx, 10, 104, 108, 16, 8);
  ctx.fill();
  // slate diamond inlay
  ctx.fillStyle = '#5e6f86';
  ctx.beginPath();
  ctx.moveTo(64, 40);
  ctx.lineTo(88, 64);
  ctx.lineTo(64, 88);
  ctx.lineTo(40, 64);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#7f92ad';
  ctx.beginPath();
  ctx.moveTo(64, 50);
  ctx.lineTo(78, 64);
  ctx.lineTo(64, 78);
  ctx.lineTo(50, 64);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#5e6f86';
  for (const [cx, cy] of [
    [18, 18],
    [110, 18],
    [18, 110],
    [110, 110],
  ] as const) {
    ctx.beginPath();
    ctx.arc(cx, cy, 4.5, 0, Math.PI * 2);
    ctx.fill();
  }
  return tileTexture(canvas);
}

/** Radial shadow blob used to ground the floating diorama on the gradient backdrop. */
export function makeShadowBlobTexture(): THREE.CanvasTexture {
  const [canvas, ctx] = canvas2d(256, 256);
  const gradient = ctx.createRadialGradient(128, 128, 20, 128, 128, 128);
  gradient.addColorStop(0, 'rgba(70, 40, 20, 0.55)');
  gradient.addColorStop(0.55, 'rgba(70, 40, 20, 0.28)');
  gradient.addColorStop(1, 'rgba(70, 40, 20, 0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 256, 256);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}
